import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { Field, FieldType, UserData } from "./types";
import React, { useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import styled from "@emotion/styled";
import { CopyToClipboardInput } from "@kaltura/mediaspace-shared-ui";
import { InviteForm } from "./form/InviteForm";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

// The steps/states of the invite modal.
enum ModalStep {
    INVITE = "invite",
    COPY_LINK = "copy-link",
}

export interface InviteProps extends SharedMenuItemProps {
    entryId: string;
    fields: Field[];
    submitUrl: string;
    showEmailOption?: boolean;
}

export interface InviteModalProps extends InviteProps {
    onClose: () => void;
}

/**
 * Modal that allows invite external users to media and copy the invite url.
 */
export const InviteModal = (props: InviteModalProps) => {
    const { entryId, submitUrl, fields, onClose, showEmailOption = false } = props;
    const [inviteLink, setInviteLink] = useState("");
    const [modalStep, setModalStep] = useState(ModalStep.INVITE);
    const [disabledFields, setDisabledFields] = useState<Record<string, boolean>>({});
    const [requiredFields, setRequiredFields] = useState(() =>
        fields.reduce(
            (acc, field) => {
                if (field.required) {
                    acc[field.id] = true;
                }
                return acc;
            },
            {} as Record<string, boolean>
        )
    );
    const [isExistingUser, setIsExistingUser] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    // Initialize react-hook-form with default values based on fields
    const methods = useForm({
        mode: "onChange",
        defaultValues: fields.reduce(
            (acc, field) => {
                acc[field.id] = field.type === FieldType.RADIO ? field.options?.[0]?.value || "" : "";
                return acc;
            },
            {} as Record<string, string>
        ),
    });

    const {
        formState: { isSubmitting, isValid },
    } = methods;

    const formId = useId();
    const { showToast } = useToastsContext();

    const onCopyLink = () => {
        showToast({
            severity: "success",
            message: translate("Link copied to clipboard."),
        });
        sendButtonAnalytics(
            "Invite user - copy link",
            ButtonClickAnalyticsType.SHARE,
            entryId,
            "invitetomedia",
            "Success state after invite"
        );
    };

    const onCancel = () => {
        if (modalStep === ModalStep.INVITE) {
            sendButtonAnalytics("Invite user - cancel", ButtonClickAnalyticsType.CLOSE);
        }
        onClose();
    };

    //Submit the invite form and generate the invite link
    const onSubmit = async (data: any) => {
        try {
            const { success, inviteUrl, message } = await postKmsData(submitUrl, { entryId, formData: data });
            if (!success) {
                if (message) {
                    console.error("Failed to generate invite link:", message);
                }
                handleError();
                return;
            }

            setInviteLink(inviteUrl);
            setModalStep(ModalStep.COPY_LINK);

            // show toast if a new user is created and email option is selected
            if (!isExistingUser && data.sendEmail) {
                showToast({
                    severity: "success",
                    message: translate("User %1 created successfully", [data.emailAutocomplete.value]),
                });
            }

            sendButtonAnalytics("Invite user - generate link", ButtonClickAnalyticsType.SAVE);
        }
        catch (e: unknown) {
            console.error(e);
            handleError();
        }
    };

    const handleError = () => {
        showToast({
            severity: "error",
            message: translate("Failed to generate invite link"),
        });
    };

    // When a user is selected from autocomplete, update and disable name fields if needed
    const onUserSelected = (user: UserData) => {
        const { id, firstName, lastName } = user;
        const isExistingUser = !!id;
        setIsExistingUser(isExistingUser);

        const updateField = (fieldId: "firstName" | "lastName", value: string) => {
            const fieldConfig = fields.find((f) => f.id === fieldId);
            if (!fieldConfig) return;

            methods.setValue(fieldId, value);

            // disable field for existing user
            setDisabledFields((prev) => ({ ...prev, [fieldId]: isExistingUser }));

            // for existing user fields are not required, for other users it depends on the field config
            setRequiredFields((prev) => ({
                ...prev,
                [fieldId]: isExistingUser ? false : fieldConfig.required ?? false,
            }));

            //clear errors if existing user
            if (isExistingUser) {
                methods.clearErrors(fieldId);
            }
        };

        updateField("firstName", firstName);
        updateField("lastName", lastName);
    };

    return (
        <>
            <Modal
                open={true}
                fullWidth={true}
                // Prevent menu item keydown propagation
                onKeyDown={(e) => e.stopPropagation()}
            >
                <ModalTitle onClose={onCancel}>
                    <Typography variant={"h3"} component={"div"}>
                        {translate("Invite users")}
                    </Typography>
                </ModalTitle>
                <ModalContent>
                    <Typography variant={"body1"}>
                        {modalStep === ModalStep.COPY_LINK
                            && translate(
                                "To enter the media page, the users will need to enter their email address as a form of authentication."
                            )}
                        {modalStep === ModalStep.INVITE
                            && translate("Add users by email, assign roles, and control their access for the session.")}
                    </Typography>

                    {modalStep === ModalStep.INVITE && (
                        <FormProvider {...methods}>
                            <InviteForm
                                fields={fields}
                                requiredFields={requiredFields}
                                disabledFields={disabledFields}
                                onUserSelected={onUserSelected}
                                onSubmit={onSubmit}
                                formId={formId}
                                showEmailOption={showEmailOption}
                            />
                        </FormProvider>
                    )}
                    {modalStep === ModalStep.COPY_LINK && (
                        <StyledCopyToClipboardInput
                            label={translate("Link to media page")}
                            text={inviteLink}
                            onCopy={onCopyLink}
                        />
                    )}
                </ModalContent>
                {modalStep === ModalStep.INVITE && (
                    <ModalActions>
                        <Button variant={"borderless"} color={"secondary"} disabled={isSubmitting} onClick={onCancel}>
                            {translate("Cancel")}
                        </Button>

                        <Button
                            type={"submit"}
                            form={formId}
                            loading={isSubmitting}
                            disabled={isSubmitting || !isValid}
                        >
                            {translate("Generate invite link")}
                        </Button>
                    </ModalActions>
                )}
            </Modal>
        </>
    );
};

const StyledCopyToClipboardInput = styled(CopyToClipboardInput)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
}));
